import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const PLANS = [
  {
    id: "essential", name: "Essential",
    monthly: 29, annual: 23, badge: "Pour démarrer",
    description: "L'entrée en matière pour structurer votre activité.",
    features: [
      "Facturation Factur-X illimitée",
      "1 utilisateur · 1 entité",
      "Rapprochement bancaire (1 banque)",
      "Journal d'audit",
    ],
    ctaLabel: "S'abonner à Essential",
    variant: "outline",
  },
  {
    id: "business", name: "Business",
    monthly: 79, annual: 63, badge: "Le plus populaire",
    popular: true,
    description: "L'équipement complet des PME en croissance.",
    features: [
      "Tout Essential",
      "5 utilisateurs · rôles RBAC",
      "Multi-banques (illimité)",
      "Achats + OCR fournisseurs",
      "Contrats + résiliations",
      "Notes de frais + cartes",
    ],
    ctaLabel: "S'abonner à Business",
    variant: "primary",
  },
  {
    id: "scale", name: "Scale",
    monthly: 199, annual: 159, badge: "DAF exigeants",
    description: "Analytique avancée et multi-entités.",
    features: [
      "Tout Business",
      "15 utilisateurs",
      "Multi-entités + consolidation",
      "Prévisionnel trésorerie 90j",
      "RH + Paie + DPAE",
      "Board Pack + API",
    ],
    ctaLabel: "S'abonner à Scale",
    variant: "outline",
  },
  {
    id: "enterprise", name: "Enterprise",
    monthly: 499, annual: 399, badge: "Sur-mesure",
    description: "SLA dédié, gouvernance renforcée, ERP connectés.",
    features: [
      "Tout Scale",
      "Utilisateurs illimités",
      "Connecteurs ERP (Cegid, Sage, SAP)",
      "SSO SAML / SCIM",
      "SLA 99,95%",
      "Customer Success Manager dédié",
    ],
    ctaLabel: "S'abonner à Enterprise",
    variant: "outline",
  },
  {
    id: "cabinet", name: "Cabinet",
    monthly: 349, annual: 279, badge: "Expertise-comptable",
    description: "Multi-dossiers illimité pour cabinets.",
    features: [
      "Dossiers clients illimités",
      "Portail collaboratif client",
      "Import FEC + export Cegid/Sage/ACD",
      "OCR apprenant par client",
      "Alertes conformité (TVA, DSN, CVAE)",
      "Marque blanche disponible",
    ],
    ctaLabel: "S'abonner à Cabinet",
    variant: "outline",
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section
      id="tarifs"
      className="py-20 lg:py-28 bg-gradient-to-b from-[#F8FAFC] to-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#4F46E5]">
            Tarifs simples, sans surprise
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B0F17]">
            Le bon plan pour chaque étape de votre croissance.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Essai gratuit de 14 jours sur tous les plans. Sans carte bancaire.
            Migration Pennylane / Sage assistée offerte.
          </p>
        </div>

        {/* Toggle */}
        <div className="mt-8 flex justify-center">
          <div
            className="inline-flex rounded-full border border-slate-200 bg-white p-1 relative"
            data-testid="pricing-billing-toggle-wrap"
          >
            <button
              onClick={() => setAnnual(false)}
              data-testid="pricing-billing-monthly"
              className={cn(
                "px-5 py-2 text-sm font-semibold rounded-full transition-all",
                !annual ? "bg-[#0B0F17] text-white" : "text-slate-600",
              )}
            >
              Mensuel
            </button>
            <button
              onClick={() => setAnnual(true)}
              data-testid="pricing-billing-annual"
              className={cn(
                "px-5 py-2 text-sm font-semibold rounded-full transition-all flex items-center gap-2",
                annual ? "bg-[#0B0F17] text-white" : "text-slate-600",
              )}
            >
              Annuel
              <span
                className={cn(
                  "text-[10px] font-bold px-1.5 py-0.5 rounded-full",
                  annual
                    ? "bg-emerald-400 text-slate-900"
                    : "bg-emerald-50 text-emerald-700",
                )}
              >
                -20%
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {PLANS.map((p) => {
            const price = annual ? p.annual : p.monthly;
            const isPopular = p.popular;
            const target =
              `/register?plan=${p.id}${annual ? "&billing=yearly" : ""}`;
            return (
              <div
                key={p.id}
                className={cn(
                  "relative rounded-2xl p-6 flex flex-col transition-all",
                  isPopular
                    ? "bg-[#0F172A] text-white border-2 border-[#0F172A] shadow-2xl shadow-slate-900/20 xl:scale-[1.03]"
                    : "bg-white border border-slate-200 hover:border-slate-300",
                )}
                data-testid={`pricing-card-${p.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-400 text-slate-900 text-[10px] font-bold uppercase tracking-widest">
                      <Sparkles className="h-3 w-3" />
                      Le plus populaire
                    </span>
                  </div>
                )}
                <div
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-widest",
                    isPopular ? "text-indigo-300" : "text-slate-400",
                    isPopular && "invisible",
                  )}
                >
                  {p.badge}
                </div>
                <div className="mt-1 text-xl font-bold">{p.name}</div>
                <p
                  className={cn(
                    "mt-1.5 text-xs min-h-[36px]",
                    isPopular ? "text-slate-300" : "text-slate-500",
                  )}
                >
                  {p.description}
                </p>

                <div className="mt-5">
                  <div className="flex items-baseline gap-1">
                    <span
                      className={cn(
                        "text-4xl font-extrabold tabular-nums",
                        isPopular ? "text-white" : "text-[#0B0F17]",
                      )}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {price}
                    </span>
                    <span
                      className={cn(
                        "text-lg font-semibold",
                        isPopular ? "text-white" : "text-[#0B0F17]",
                      )}
                    >
                      €
                    </span>
                    <span
                      className={cn(
                        "text-xs ml-1",
                        isPopular ? "text-slate-400" : "text-slate-500",
                      )}
                    >
                      HT / mois
                    </span>
                  </div>
                  {annual && (
                    <div
                      className={cn(
                        "text-[11px] mt-1",
                        isPopular ? "text-emerald-300" : "text-emerald-600",
                      )}
                    >
                      soit {p.annual * 12} € HT/an — économisez {(p.monthly - p.annual) * 12}€
                    </div>
                  )}
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className={cn(
                        "flex items-start gap-2 text-xs",
                        isPopular ? "text-slate-200" : "text-slate-700",
                      )}
                    >
                      <Check
                        className={cn(
                          "h-3.5 w-3.5 mt-0.5 shrink-0",
                          isPopular ? "text-emerald-400" : "text-emerald-600",
                        )}
                        strokeWidth={2.5}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to={target}
                  data-testid={`subscribe-plan-${p.id}-button`}
                  className={cn(
                    "mt-6 group inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-all",
                    isPopular
                      ? "bg-white text-[#0B0F17] hover:bg-slate-100 shadow-lg"
                      : p.variant === "primary"
                      ? "bg-[#0B0F17] text-white hover:bg-[#1E293B]"
                      : "bg-[#0B0F17]/5 text-[#0B0F17] hover:bg-[#0B0F17] hover:text-white border border-transparent",
                  )}
                >
                  {p.ctaLabel}
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs text-slate-500">
          Tarifs HT · TVA collectée par Stripe · Paiement sécurisé Stripe
          Managed Payments · Résiliable à tout moment
        </div>
      </div>
    </section>
  );
}
